/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import axios from 'axios';
import get from 'lodash/get';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { STATUS, SecurityHelper } from 'sarsaparilla';

import { getProcessedErrorMessage } from '../utils/handleBackEndError';
import { FALLBACK_PERMIT_MAPPING } from '../../src/constants'; // WARNING - use relative imports here due to being used in common uis that do not support absolute

export const permitContentSelector = {
    permitMapping: (state) => state?.permitContent?.permitMapping.data,
    permitMappingTTL: (state) => state?.permitContent?.permitMapping.ttl,
};

export const permitSelector = {
    getData: (state) => state.permitContent?.permit?.data ?? {},
    getError: (state) => state.permitContent.permit.error,
    isFetching: (state) => state.permitContent.permit.loadingStatus === STATUS.PENDING,
};

export const getPermitContentUrl = (permitId) => {
    return `${process.env.API}/permitcontent/${permitId}`;
};

export const loadPermitMapping = createAsyncThunk(
    'permitContent/loadPermitMapping',
    async (_, { rejectWithValue }) => {
        try {
            const permitMappingResponse = await axios.get(
                `${process.env.API}/permitcontent/permitmapping`
            );
            return permitMappingResponse?.data?.payload;
        } catch (error) {
            const errorMessage = get(error, 'response.data.error', null);
            // TODO - add alert / sentry warning for this occurrence. alternatively after completed migration, we can drop the fallback and
            // load it via webpack on compile as a fallback (instead of hardcoded)
            return rejectWithValue({
                error: errorMessage || get(error, 'response.statusText', 'Server error'),
                permitMapping: FALLBACK_PERMIT_MAPPING,
            });
        }
    },
    {
        condition: (_, { getState }) => {
            const ttl = permitContentSelector.permitMappingTTL(getState()) || null;
            // return truthy in order to fetch, if ttl is unset OR if we are now past the TTL
            return !ttl || Date.now() > ttl;
        },
    }
);

// Created because for some reason loadPermitMapping call above (createAsyncThunk approach) sometimes doesn't even get called
// in some places. e.g. Cancelling lottery application (R1S-46665)
export const loadPermitMappingNonAsyncThunk = () => {
    return async function () {
        const url = `${process.env.API}/permitcontent/permitmapping`;
        try {
            const permitMappingResponse = await axios.get(url, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            });
            return permitMappingResponse?.data?.payload;
        } catch (error) {
            const errorMessage = get(error, 'permitMappingResponse.data.error', null);
            return {
                error:
                    errorMessage ||
                    get(error, 'permitMappingResponse.statusText', 'Server error'),
                permitMapping: FALLBACK_PERMIT_MAPPING,
            };
        }
    };
};

export const loadPermitContent = createAsyncThunk(
    'permitContent/loadPermitContent',
    async (permitId, { rejectWithValue }) => {
        try {
            const permitContentResponse = await axios.get(getPermitContentUrl(permitId));
            return permitContentResponse?.data?.payload;
        } catch (error) {
            return rejectWithValue({
                error: getProcessedErrorMessage(error),
            });
        }
    }
);

const contentSlice = createSlice({
    /* eslint-disable no-param-reassign */
    name: 'permitContent',
    initialState: {
        permitMapping: {
            error: null,
            data: {},
            ttl: null,
        },
        permit: {
            error: null,
            data: {},
            hasFirstRequestFinished: false,
            loadingStatus: STATUS.IDLE,
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadPermitMapping.fulfilled, (state, action) => {
                state.permitMapping.data = action.payload;
                state.permitMapping.ttl = Date.now() + 60000;
            })
            .addCase(loadPermitMapping.rejected, (state, action) => {
                state.permitMapping.error = action.payload.error;
                state.permitMapping.data = action.payload.permitMapping;
                state.permitMapping.ttl = null;
            })
            .addCase(loadPermitContent.fulfilled, (state, action) => {
                state.permit.data = action.payload;
                state.permit.loadingStatus = STATUS.SUCCESS;
                state.permit.hasFirstRequestFinished = true;
            })
            .addCase(loadPermitContent.pending, (state) => {
                state.permit.loadingStatus = STATUS.PENDING;
            })
            .addCase(loadPermitContent.rejected, (state, action) => {
                state.permit.error = action.payload.error;
                state.permit.loadingStatus = STATUS.ERROR;
                state.permit.hasFirstRequestFinished = true;
            });
    },
    reducers: {
        clearPermitMapping: (state) => {
            state.permitMapping.data = {};
            state.permitMapping.error = null;
            state.permitMapping.ttl = null;
        },
    },
});

export const { clearPermitMapping } = contentSlice.actions;

// optionally can combine reducers if needed later, no need at present
export const reducers = contentSlice.reducer;
